<template>
  <div v-if="open" class="modal fade show d-block warehouse-modal" tabindex="-1" aria-modal="true" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ isEdit ? $t("warehouse_page.edit_chip") : $t("warehouse_page.new_chip") }}</span>
            <h5 class="modal-title fw-bold mt-3 mb-1">
              {{ isEdit ? $t("warehouse_page.update_title") : $t("warehouse_page.create_title") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("warehouse_page.modal_description") }}
            </p>
          </div>
          <button type="button" class="btn-close shadow-none" :aria-label="$t('common.close')" @click="$emit('close')"></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("warehouse_page.code") }}</label>
                <span class="field-caption">{{ $t("warehouse_page.code_caption") }}</span>
              </div>
              <div class="reference-input-wrap">
                <input
                  :value="form.code"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': errors.code }"
                  :placeholder="$t('warehouse_page.code_placeholder')"
                  @input="$emit('update-field', 'code', ($event.target as HTMLInputElement).value)"
                />
                <button
                  v-if="!isEdit"
                  type="button"
                  class="btn btn-light generate-code-btn"
                  :disabled="loading"
                  @click="$emit('generate-code')"
                >
                  <i class="ri-refresh-line me-1"></i>
                  {{ $t("warehouse_page.generate") }}
                </button>
              </div>
              <div v-if="errors.code" class="invalid-feedback d-block">{{ errors.code[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("warehouse_page.name") }}</label>
                <span class="field-caption">{{ $t("warehouse_page.name_caption") }}</span>
              </div>
              <input
                :value="form.name"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.name }"
                :placeholder="$t('warehouse_page.name_placeholder')"
                @input="$emit('update-field', 'name', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.name" class="invalid-feedback d-block">{{ errors.name[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("warehouse_page.phone") }}</label>
                <span class="field-caption">{{ $t("warehouse_page.phone_caption") }}</span>
              </div>
              <input
                :value="form.phone"
                type="text"
                class="form-control modal-input"
                :placeholder="$t('warehouse_page.phone_placeholder')"
                @input="$emit('update-field', 'phone', ($event.target as HTMLInputElement).value)"
              />
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("warehouse_page.email") }}</label>
                <span class="field-caption">{{ $t("warehouse_page.email_caption") }}</span>
              </div>
              <input
                :value="form.email"
                type="email"
                class="form-control modal-input"
                :placeholder="$t('warehouse_page.email_placeholder')"
                @input="$emit('update-field', 'email', ($event.target as HTMLInputElement).value)"
              />
            </div>

            <div class="col-12">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("warehouse_page.address") }}</label>
                <span class="field-caption">{{ $t("warehouse_page.address_caption") }}</span>
              </div>
              <textarea
                :value="form.address"
                rows="4"
                class="form-control modal-input modal-textarea"
                :placeholder="$t('warehouse_page.address_placeholder')"
                @input="$emit('update-field', 'address', ($event.target as HTMLTextAreaElement).value)"
              ></textarea>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("warehouse_page.status") }}</label>
                <span class="field-caption">{{ $t("warehouse_page.status_caption") }}</span>
              </div>
              <select
                :value="form.status ?? 1"
                class="form-select modal-input"
                @change="$emit('update-field', 'status', Number(($event.target as HTMLSelectElement).value))"
              >
                <option :value="1">{{ $t("warehouse_page.status_active") }}</option>
                <option :value="0">{{ $t("warehouse_page.status_inactive") }}</option>
              </select>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
            <button type="button" class="btn btn-light px-4" @click="$emit('close')">
              {{ $t("common.cancel") }}
            </button>
            <button type="button" class="btn style-one px-4 h-55" :disabled="loading" @click="$emit('save')">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? $t("warehouse_page.saving") : isEdit ? $t("warehouse_page.update_action") : $t("warehouse_page.create_action") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { Warehouse } from "@/services/warehouse";

export default defineComponent({
  name: "CreateWarehouseModal",
  emits: ["close", "save", "generate-code", "update-field"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object as PropType<Warehouse>,
      required: true,
    },
    errors: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
});
</script>

<style scoped>
.warehouse-modal {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  color: #6b7280;
  font-size: 12px;
}

.reference-input-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: stretch;
}

.modal-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #fff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
}

.modal-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.modal-textarea {
  min-height: 110px;
  padding-top: 16px;
}

.generate-code-btn {
  min-height: 54px;
  padding: 0 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #f8faff;
  font-weight: 600;
  white-space: nowrap;
}
</style>
